import React from 'react';

function DataTable({
  columns = [],
  rows = [],
  rowKey = 'id',
  emptyMessage = 'No data',
  pageSize = 10,
  page = 1,
  onPageChange,
  showPagination = true,
  rowActions = [],
  onRowClick,
  enableSelection = false,
  selectedRowIds,
  onSelectedRowIdsChange,
  tableClassName = '',
  pageSizeOptions,
  onPageSizeChange,
}) {
  const [internalPage, setInternalPage] = React.useState(page);
  const [internalSelectedIds, setInternalSelectedIds] = React.useState([]);

  React.useEffect(() => {
    setInternalPage(page);
  }, [page]);

  const activePage = onPageChange ? page : internalPage;
  const selectedIds = selectedRowIds ?? internalSelectedIds;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const boundedPage = Math.max(1, Math.min(activePage, totalPages));
  const startIndex = (boundedPage - 1) * pageSize;
  const pagedRows = showPagination ? rows.slice(startIndex, startIndex + pageSize) : rows;

  const setPageValue = React.useCallback(
    nextPage => {
      if (onPageChange) {
        onPageChange(nextPage);
      } else {
        setInternalPage(nextPage);
      }
    },
    [onPageChange]
  );

  const align = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

  const setSelectedIds = React.useCallback(
    nextIds => {
      if (onSelectedRowIdsChange) {
        onSelectedRowIdsChange(nextIds);
      } else {
        setInternalSelectedIds(nextIds);
      }
    },
    [onSelectedRowIdsChange]
  );

  const toggleSelectAll = checked => {
    if (!checked) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(pagedRows.map(row => row[rowKey]));
  };

  const toggleSelectRow = (id, checked) => {
    if (checked) {
      setSelectedIds(Array.from(new Set([...selectedIds, id])));
      return;
    }
    setSelectedIds(selectedIds.filter(value => value !== id));
  };

  const areAllVisibleRowsSelected =
    pagedRows.length > 0 && pagedRows.every(row => selectedIds.includes(row[rowKey]));

  return (
    <div className="overflow-visible rounded-xl border border-gray-700" style={{ background: 'var(--axial-card)' }}>
      <div className="overflow-x-auto">
        <table className={`min-w-full divide-y divide-gray-700 text-sm ${tableClassName}`}>
          <thead className="sticky top-0 z-10" style={{ background: 'var(--axial-table-header)' }}>
            <tr>
              {enableSelection && (
                <th className="w-10 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    aria-label="Select all rows"
                    checked={areAllVisibleRowsSelected}
                    onChange={event => toggleSelectAll(event.target.checked)}
                  />
                </th>
              )}
              {columns.map(column => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400 ${align[column.align] || 'text-left'} ${column.className || ''}`}
                >
                  {column.header}
                </th>
              ))}
              {rowActions.length > 0 && (
                <th className="w-14 px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800" style={{ background: 'var(--axial-table-body)' }}>
            {pagedRows.length === 0 && (
              <tr>
                <td
                  colSpan={
                    columns.length + (enableSelection ? 1 : 0) + (rowActions.length > 0 ? 1 : 0)
                  }
                  className="px-4 py-12 text-center text-sm text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
            {pagedRows.map(row => (
              <tr
                key={row[rowKey]}
                className={`hover:bg-gray-800/50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {enableSelection && (
                  <td className="px-4 py-3 align-middle">
                    <input
                      type="checkbox"
                      aria-label={`Select row ${row[rowKey]}`}
                      checked={selectedIds.includes(row[rowKey])}
                      onChange={event => toggleSelectRow(row[rowKey], event.target.checked)}
                    />
                  </td>
                )}
                {columns.map(column => (
                  <td key={column.key} className={`px-4 py-3 align-middle text-gray-200 ${align[column.align] || 'text-left'} ${column.cellClassName || ''}`}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
                {rowActions.length > 0 && (
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex" onClick={e => e.stopPropagation()}>
                      {rowActions[0]?.render ? rowActions[0].render(row) : (
                        <button
                          type="button"
                          aria-label="Row actions"
                          className="h-7 w-7 rounded border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500"
                          title={rowActions.map(action => action.label).join(', ')}
                          onClick={e => { e.stopPropagation(); rowActions[0]?.onClick?.(row); }}
                        >
                          ...
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <div className="flex items-center justify-between border-t border-gray-700 px-4 py-3 text-xs text-gray-400">
          <div className="flex items-center gap-3">
            <p>
              Showing {rows.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + pageSize, rows.length)} of {rows.length}
            </p>
            {pageSizeOptions?.length > 0 && onPageSizeChange && (
              <label className="flex items-center gap-1.5">
                Show
                <select
                  value={pageSize}
                  onChange={event => onPageSizeChange(Number(event.target.value))}
                  className="h-7 rounded border border-gray-700 bg-transparent px-1.5 text-gray-300"
                >
                  {pageSizeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="h-8 px-2 rounded border border-gray-700 hover:border-gray-500 disabled:opacity-50"
              disabled={boundedPage <= 1}
              onClick={() => setPageValue(boundedPage - 1)}
            >
              Prev
            </button>
            <span>
              Page {boundedPage} / {totalPages}
            </span>
            <button
              type="button"
              className="h-8 px-2 rounded border border-gray-700 hover:border-gray-500 disabled:opacity-50"
              disabled={boundedPage >= totalPages}
              onClick={() => setPageValue(boundedPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
